import React, { useCallback, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import ReactPaginate from 'react-paginate';
import cn from 'classnames';
import moment from 'moment';

import Permitted from 'components/core/Permitted.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import PERM from 'constants/core/perm.const';
import { DEPOSIT_TYPE_TRANSLATION_KEYS } from 'constants/depositType.const';
import STAFF_GROUP from 'constants/staff/group.const';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import { CashupLogItem } from 'cashup/CashupLogItem';
import TooltipTransactionLog from 'cashup/components/TooltipTransactionLog.component';
import DepositLogItem from 'cashup/DepositLogItem';
import PettyCashLogItem from 'cashup/PettyCashLogItem';
import VoucherSoldItem from 'cashup/VoucherSoldItem';

import CN from './CashupLogs.module.scss';

const L = logger('CashupLogs');

const DEFAULT_SOURCE_TYPE_NAME = 'cashUp.cashupLogs.depositItems.unknownSource';
const USE_MODAL_REFUND = false;

const CashupLogs = (props) => {
  const {
    reconcileList,
    transactions,
    rsBeGetCashup,
    transactionLog,
    onCheckout,
    changePage,
    selectedStaff,
    onVoidTransaction,
    onViewReceipt,
    pageCount,
    depositData,
    voucherSoldList,
    salonId,
    cashUpTotals,
    currentPage,
    selectedStaffGroup,
    clearTransactionLog,
    getTransactionsByStylist,
    timeZone,
    exportTransactionLog,
    selectedStylistIds,
    endDate,
    startDate,
  } = props;

  const [subTab, setSubTab] = useState(0);

  const csvInstanceRef = useRef(null);

  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const handleExportTransaction = useCallback(async () => {
    const group = selectedStaffGroup || STAFF_GROUP.all;
    const isAllStaff = group === STAFF_GROUP.all;

    L.debug('handleExportTransaction()', isAllStaff, { selectedStaffGroup, group });

    try {
      await (isAllStaff ? exportTransactionLog(salonId, startDate, endDate, timeZone) : getTransactionsByStylist(salonId, startDate, endDate, timeZone, selectedStylistIds));

      const el = document.querySelector('.csvlink a');

      if (el) {
        el.click();
        el.style.display = 'none';
        clearTransactionLog();
      }
    } catch (err) {
      L.warn('handleExportTransaction()', err);
    }
  }, [clearTransactionLog, endDate, exportTransactionLog, getTransactionsByStylist, salonId, selectedStaffGroup, selectedStylistIds, startDate, timeZone]);

  const selectTab = (subTab) => void setSubTab(subTab);

  const totalPartCash = cashUpTotals.totalCash || 0;
  const totalOnlineAmount = cashUpTotals.totalOnlineCard || 0;
  const totalCardAmount = cashUpTotals.totalSalonCard || 0;
  const totalOtherAmount = cashUpTotals.totalOther || 0;
  const totalDeposit = cashUpTotals.totalDepositsRedeemed || 0;
  const totalVoucher = cashUpTotals.totalVouchersRedeemed || 0;
  const totalBill = cashUpTotals.totalRevenue || 0;
  const totalRev = cashUpTotals.totalRevenue - cashUpTotals.totalTip || 0;
  const totalTip = cashUpTotals.totalTip || 0;

  const isCsvLinkRendered = fromAnyToBool(transactionLog && !rsBeGetCashup);
  const isTxLogRendered = isFalsy(transactionLog);

  const hasTransactionLog = fromAnyToBool(transactionLog);
  const hasData = fromAnyToBool(depositData?.depositsLogs?.length || transactions?.length || reconcileList?.results?.length || voucherSoldList?.length);

  return (
    <div
      data-bem="CashupLogs"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="CashupLogs__download-link" data-is-export-v1={isCsvLinkRendered} data-is-export-v2={isTxLogRendered} className="csvlink">
        {isCsvLinkRendered && (
          <Permitted value={PERM.canViewDailyCashupAllStaff}>
            <CSVLink data={transactionLog} filename="transLog.csv" ref={csvInstanceRef}>
              {t('cashUp.cashupLogs.buttons.downloadFullReport')}
            </CSVLink>
          </Permitted>
        )}

        {isTxLogRendered && (
          <Permitted value={PERM.canViewDailyCashupAllStaff}>
            <div data-bem="CashupLogs__transaction-log--local" className="button-csv">
              <button onClick={handleExportTransaction} disabled={!hasData}>
                {!hasData ? t('cashUp.cashupLogs.buttons.noData') : hasTransactionLog ? t('cashUp.cashupLogs.buttons.gettingData') : t('cashUp.cashupLogs.buttons.downloadFullReport')}
              </button>
              <TooltipTransactionLog />
            </div>
          </Permitted>
        )}
      </div>

      <div data-bem="CashupLogs__tabs" className="csh-list-wrap clearfix tab-container">
        <span
          data-bem="CashupLogs__tab-bills"
          role="tab"
          tabIndex="0"
          onClick={() => selectTab(0)}
          className={cn({
            'csh-list-tab': true,
            active: subTab === 0,
          })}
        >
          {t('cashUp.cashupLogs.tabs.clientBills')}
        </span>
        <span
          data-bem="CashupLogs__tab-deposits"
          role="tab"
          tabIndex="-1"
          onClick={() => selectTab(1)}
          className={cn({
            'csh-list-tab hidden-for-print': true,
            active: subTab === 1,
          })}
        >
          {t('cashUp.cashupLogs.tabs.depositsLog')}
        </span>
        <span
          data-bem="CashupLogs__tab-cash"
          role="tab"
          tabIndex="-2"
          onClick={() => selectTab(2)}
          className={cn({
            'csh-list-tab hidden-for-print': true,
            active: subTab === 2,
          })}
        >
          {t('cashUp.cashupLogs.tabs.pettyCashLog')}
        </span>
        <span
          data-bem="CashupLogs__tab-vouchers"
          role="tab"
          tabIndex="-3"
          onClick={() => selectTab(3)}
          className={cn({
            'csh-list-tab hidden-for-print': true,
            active: subTab === 3,
          })}
        >
          {t('cashUp.cashupLogs.tabs.vouchersSold')}
        </span>
      </div>

      {subTab === 0 && (
        <>
          <div
            className={cn({
              [CN.overflowContainer]: true,
              [CN.withPaginate]: pageCount > 1,
            })}
            data-bem="CashupLogs__container-bills"
          >
            <div className={CN.stickyHeader} data-bem="CashupLogs__header-bills">
              <div className={CN.tableHeader}>
                <div className={CN.headerFixedCell}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.time')}</span>
                  </div>
                </div>

                <div
                  className={cn({
                    [CN.cellHeader]: true,
                    [CN.headerFixedSecondCell]: true,
                  })}
                >
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.clientName')}</span>
                  </div>
                  <div className={CN.columnTotal}>{t('cashUp.cashupLogs.tableColumns.total')}</div>
                </div>
                <div className={CN.cellHeader}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.cash')}</span>
                  </div>
                  <div className={CN.columnTotal}>
                    <CurrencySign value={totalPartCash} />
                  </div>
                </div>
                <div className={CN.cellHeader}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.card')}</span>
                    <span>{t('cashUp.cashupLogs.tableColumns.online')}</span>
                  </div>
                  <div className={CN.columnTotal}>
                    <CurrencySign value={totalOnlineAmount} />
                  </div>
                </div>
                <div className={CN.cellHeader}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.card')}</span>
                    <span>{t('cashUp.cashupLogs.tableColumns.inStore')}</span>
                  </div>
                  <div className={CN.columnTotal}>
                    <CurrencySign value={totalCardAmount} />
                  </div>
                </div>
                <div className={CN.cellHeader}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.other')}</span>
                  </div>
                  <div className={CN.columnTotal}>
                    <CurrencySign value={totalOtherAmount} />
                  </div>
                </div>
                <div className={CN.cellHeader}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.deposit')}</span>
                    <span>{t('cashUp.cashupLogs.tableColumns.redeemed')}</span>
                  </div>
                  <div className={CN.columnTotal}>
                    <CurrencySign value={totalDeposit} />
                  </div>
                </div>
                <div className={CN.cellHeader}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.voucher')}</span>
                    <span>{t('cashUp.cashupLogs.tableColumns.redeemed')}</span>
                  </div>
                  <div className={CN.columnTotal}>
                    <CurrencySign value={totalVoucher} />
                  </div>
                </div>
                <div className={CN.cellHeader}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.totalBill')}</span>
                    <span>{t('cashUp.cashupLogs.tableColumns.inclTips')}</span>
                  </div>
                  <div className={CN.columnTotal}>
                    <CurrencySign value={totalBill} />
                  </div>
                </div>
                <div className={CN.cellHeader}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.tips')}</span>
                  </div>
                  <div className={CN.columnTotal}>
                    <CurrencySign value={totalTip} />
                  </div>
                </div>
                <div className={CN.cellHeader}>
                  <div className={CN.columnTitle}>
                    <span>{t('cashUp.cashupLogs.tableColumns.revenue')}</span>
                  </div>
                  <div className={CN.columnTotal}>
                    <CurrencySign value={totalRev} />
                  </div>
                </div>
                <div className={CN.cellHeader}></div>
              </div>
            </div>

            <div className={CN.scrollBody}>
              {selectedStaffGroup && selectedStaffGroup !== STAFF_GROUP.all && selectedStaffGroup !== STAFF_GROUP.salon && (
                <div className={CN.headerInfoText}>
                  <span className={CN.textBold}>{t('cashUp.cashupLogs.noteMessagePart1')}</span> {t('cashUp.cashupLogs.noteMessagePart2')}
                </div>
              )}

              {!rsBeGetCashup &&
                transactions.map((tx) =>
                  transactions.filter((c) => c.created_from === tx.id).length >= 1 ? null : (
                    <CashupLogItem
                      data={tx}
                      key={tx.id}
                      onViewReceipt={onViewReceipt}
                      onCheckout={onCheckout}
                      onVoidTransaction={onVoidTransaction}
                      reCheckoutVisible={!transactions.find((c) => c.created_from === tx.created_from && c.id !== tx.id)}
                      selectedStaff={selectedStaff}
                    />
                  ),
                )}
            </div>
          </div>
          <div className={CN.paginate}>
            {pageCount > 1 && (
              <div className="csh-list-pagin clearfix">
                <ReactPaginate
                  previousLabel=""
                  nextLabel=""
                  previousLinkClassName="csh-list-pagin-btn ico-chevron-left"
                  nextLinkClassName="csh-list-pagin-btn ico-chevron-right"
                  disabledClassName="disabled"
                  breakLabel={<>...</>}
                  breakClassName="break-me"
                  pageCount={pageCount}
                  forcePage={currentPage}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={changePage}
                  containerClassName="pagination"
                  subContainerClassName="pages pagination"
                  activeClassName="active"
                />
              </div>
            )}
          </div>
        </>
      )}

      {subTab === 1 && (
        <div className={CN.overflowContainer} data-bem="CashupLogs__container-deposits">
          <div className={CN.stickyHeader} data-bem="CashupLogs__header-deposits">
            <div
              className={cn({
                [CN.tableHeader]: true,
                [CN.autoWidth]: true,
              })}
            >
              <div
                className={cn({
                  [CN.headerFixedCell]: true,
                  [CN.shadow]: true,
                })}
              >
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.time')}</span>
                  <span>{t('cashUp.cashupLogs.tableColumns.created')}</span>
                </div>
              </div>

              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.clientName')}</span>
                </div>
              </div>
              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.amount')}</span>
                  <span>{t('cashUp.cashupLogs.tableColumns.deposited')}</span>
                </div>
              </div>
              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.depositType')}</span>
                </div>
              </div>
              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.depositStatus')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={CN.scrollBody}>{depositData && depositData.depositsLogs.map((deposit) => <DepositLogItem deposit={deposit} key={deposit.id} />)}</div>
        </div>
      )}

      {subTab === 2 && (
        <div className={CN.overflowContainer} data-bem="CashupLogs__pane-cash">
          <div className={CN.stickyHeader}>
            <div
              className={cn({
                [CN.tableHeader]: true,
                [CN.autoWidth]: true,
              })}
            >
              <div
                className={cn({
                  [CN.headerFixedCell]: true,
                  [CN.shadow]: true,
                })}
              >
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.date')}</span>
                </div>
              </div>
              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.pettyCash')}</span>
                </div>
              </div>
              <div
                className={cn({
                  [CN.cellHeader]: true,
                  [CN.lgWidth]: true,
                })}
              >
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.notes')}</span>
                </div>
              </div>
            </div>
          </div>
          <div className={CN.scrollBody}>
            {reconcileList && reconcileList.results
              ? reconcileList.results
                  .sort((e1, e2) => (moment(e1.day) >= moment(e2.day) ? 1 : -1))
                  .map((petty) => <PettyCashLogItem key={petty.day} date={petty.day} pettyCash={<CurrencySign value={petty.pettyCash} />} notes={petty.notes} />)
              : null}
          </div>
        </div>
      )}

      {subTab === 3 && (
        <div className={CN.overflowContainer} data-bem="CashupLogs__pane-vouchers">
          <div className={CN.stickyHeader} data-bem="CashupLogs__header-bills">
            <div
              className={cn({
                [CN.tableHeader]: true,
                [CN.medWidth]: true,
              })}
            >
              <div
                className={cn({
                  [CN.headerFixedCell]: true,
                  [CN.shadow]: true,
                })}
              >
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.dateSold')}</span>
                </div>
              </div>

              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.voucherCode')}</span>
                </div>
              </div>
              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.purchasedBy')}</span>
                </div>
              </div>
              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.soldBy')}</span>
                </div>
              </div>
              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.amount')}</span>
                </div>
              </div>

              <div className={CN.cellHeader}>
                <div className={CN.columnTitle}>
                  <span>{t('cashUp.cashupLogs.tableColumns.paidWith')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={CN.scrollBody}>
            {isFullArray(voucherSoldList)
              ? voucherSoldList
                  .sort((e1, e2) => (moment(e1.day) >= moment(e2.day) ? 1 : -1))
                  .map((v, index) => (
                    <VoucherSoldItem
                      key={createNodeKey({ index, unique: v.number })}
                      date={v.created_at}
                      codeNumber={v.number}
                      purchasedBy={v.purchased_by}
                      soldBy={v.sold_by_stylist_name}
                      amount={<CurrencySign value={v.amount} />}
                      paid={t(DEPOSIT_TYPE_TRANSLATION_KEYS[v.source]) || t(DEFAULT_SOURCE_TYPE_NAME)}
                    />
                  ))
                  .reverse()
              : null}
          </div>
        </div>
      )}

      {USE_MODAL_REFUND && (
        <SalonModal tag="CashupLogs__modal--refund" isOpen withFooter>
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <Button variant="empty" onClick={alwaysNoop}>
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <div className={CN.modalImage}>
              <IconStatus variant="info" width="lg">
                <Icon variant="refund" width="l1" />
              </IconStatus>
            </div>
            <div className={CN.modalContent}>
              <div className={CN.title}>{t('cashUp.cashupLogs.refundModal.title')}</div>
              <span className={CN.modalText}>
                {t('cashUp.cashupLogs.refundModal.descriptionPart1')}
                <span className={CN.textFocused}>
                  {/* TODO: calculate the refunded value*/}
                  <Space>
                    <CurrencySign value={40} />
                  </Space>
                </span>
                <Space>{t('cashUp.cashupLogs.refundModal.descriptionPart2')}</Space>
                <br />
                <span className={CN.textFocused}>{t('cashUp.cashupLogs.refundModal.descriptionPart3', { card: salonId })}</span>
              </span>
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <Button variant="primaryForce" onClick={alwaysNoop}>
              <span className={CN.buttonGap}>{t('cashUp.cashupLogs.buttons.refundBill')}</span>
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}
    </div>
  );
};

CashupLogs.defaultProps = {
  reconcileList: null,
  voucherSoldList: null,
};

export default CashupLogs;
